import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

import { sceneAtProgress } from './sceneState';
import { SCENES } from './planetSections';

const VERTEX_SHADER = `
  varying vec3 vNormal;
  varying vec3 vWorldPosition;

  void main() {
    vNormal = normalize(mat3(modelMatrix) * normal);
    vec4 worldPosition = modelMatrix * vec4(position, 1.0);
    vWorldPosition = worldPosition.xyz;
    gl_Position = projectionMatrix * viewMatrix * worldPosition;
  }
`;

const FRAGMENT_SHADER = `
  uniform vec3 uColor;
  uniform float uOpacity;
  uniform float uPower;
  uniform float uTime;

  varying vec3 vNormal;
  varying vec3 vWorldPosition;

  void main() {
    vec3 viewDirection = normalize(cameraPosition - vWorldPosition);
    float fresnel = pow(1.0 - max(dot(normalize(vNormal), viewDirection), 0.0), uPower);
    float shimmer = 0.96 + sin(uTime * 0.72 + vWorldPosition.y * 4.0) * 0.04;
    gl_FragColor = vec4(uColor, fresnel * uOpacity * shimmer);
  }
`;

function createUniforms(color: string, opacity: number, power: number) {
  return {
    uColor: { value: new THREE.Color(color) },
    uOpacity: { value: opacity },
    uPower: { value: power },
    uTime: { value: 0 },
  };
}

export function Atmosphere() {
  const innerRef = useRef<THREE.ShaderMaterial | null>(null);
  const outerRef = useRef<THREE.ShaderMaterial | null>(null);
  const innerUniforms = useMemo(
    () => createUniforms(SCENES[0].atmosphereColor, 0.34, 2.25),
    [],
  );
  const outerUniforms = useMemo(
    () => createUniforms(SCENES[0].atmosphereColor, 0.15, 3.6),
    [],
  );
  const targetColor = useMemo(
    () => new THREE.Color(SCENES[0].atmosphereColor),
    [],
  );
  const nextColor = useMemo(() => new THREE.Color(), []);

  useFrame((state) => {
    const p =
      typeof window !== 'undefined' && typeof window.__planetProgress === 'number'
        ? window.__planetProgress
        : 0;
    const { idx, blendIn } = sceneAtProgress(p);
    const scene = SCENES[idx] ?? SCENES[0];
    const next = SCENES[Math.min(idx + 1, SCENES.length - 1)];
    const edge = blendIn > 0.7 ? (blendIn - 0.7) / 0.3 : 0;

    targetColor.set(scene.atmosphereColor);
    if (edge > 0 && idx < SCENES.length - 1) {
      targetColor.lerp(nextColor.set(next.atmosphereColor), edge);
    }

    const reduced = window.__planetReducedMotion === true;
    const pulse = reduced ? 1 : 1 + Math.sin(state.clock.elapsedTime * 0.82) * 0.055;
    const innerOpacity = scene.atmosphereOpacity * 2.15 * pulse;
    const outerOpacity = scene.atmosphereOpacity * 0.92 * pulse;

    for (const material of [innerRef.current, outerRef.current]) {
      if (!material) continue;
      (material.uniforms.uColor.value as THREE.Color).lerp(targetColor, 0.075);
      material.uniforms.uTime.value = state.clock.elapsedTime;
    }
    if (innerRef.current) {
      innerRef.current.uniforms.uOpacity.value = THREE.MathUtils.lerp(
        innerRef.current.uniforms.uOpacity.value,
        innerOpacity,
        0.08,
      );
    }
    if (outerRef.current) {
      outerRef.current.uniforms.uOpacity.value = THREE.MathUtils.lerp(
        outerRef.current.uniforms.uOpacity.value,
        outerOpacity,
        0.08,
      );
    }
  });

  return (
    <group name="atmosphere">
      <mesh scale={1.075} raycast={() => null}>
        <sphereGeometry args={[1.7, 48, 32]} />
        <shaderMaterial
          ref={innerRef}
          uniforms={innerUniforms}
          vertexShader={VERTEX_SHADER}
          fragmentShader={FRAGMENT_SHADER}
          transparent
          depthWrite={false}
          blending={THREE.AdditiveBlending}
          side={THREE.FrontSide}
        />
      </mesh>
      <mesh scale={1.16} raycast={() => null}>
        <sphereGeometry args={[1.7, 40, 28]} />
        <shaderMaterial
          ref={outerRef}
          uniforms={outerUniforms}
          vertexShader={VERTEX_SHADER}
          fragmentShader={FRAGMENT_SHADER}
          transparent
          depthWrite={false}
          blending={THREE.AdditiveBlending}
          side={THREE.FrontSide}
        />
      </mesh>
    </group>
  );
}

export default Atmosphere;
